import { useMemo } from "react";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { Task } from "@common/types/task";
import { ViewDensity } from "@common/types/views/views";
import { pluralize } from "@common/helpers/string";
import { useImportedAccounts } from "@api/accountingIntegration.api";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewConfig } from "@lib/views";
import { ImportedAccountCell } from "../shared/ImportedAccountCell";
import { TaskBodyCommandIsland } from "../TaskBodyCommandIsland";
import taskBodyTableStyles from "../TaskBodyTable.module.scss";
import styles from "./ConnectAccountTaskBody.module.scss";

interface ConnectAccountTaskBodyProps {
    selectedTask: Task;
}

enum ConnectAccountColumn {
    ACCOUNT = "account",
}

const connectAccountViewConfig: ViewConfig<
    ImportedAccount,
    ConnectAccountColumn,
    never
> = {
    columns: {
        [ConnectAccountColumn.ACCOUNT]: {
            accessorKey: "name",
            name: "Account",
            header: "Account",
            immutable: true,
            minSize: 200,
            maxSize: 1200,
            cell: ImportedAccountCell,
        },
    },
};

const connectAccountView = {
    filters: {},
    columns: [{ id: ConnectAccountColumn.ACCOUNT }],
    options: {
        density: ViewDensity.MD,
    },
};

export const ConnectAccountTaskBody = ({
    selectedTask,
}: ConnectAccountTaskBodyProps) => {
    const entityId = selectedTask.entityIds?.[0];

    const { data: importedAccountsData, isLoading } = useImportedAccounts(
        selectedTask.workspaceId,
        entityId,
        { orderBy: "name" },
    );

    const importedAccounts = useMemo(
        () =>
            importedAccountsData?.accounts.filter(
                (account) =>
                    account.accountConnectionTaskId === selectedTask.id,
            ) ?? [],
        [importedAccountsData, selectedTask.id],
    );

    const institutionName =
        importedAccounts[0]?.detectedInstitutionName ?? null;

    if (isLoading) {
        return null;
    }

    const count = importedAccounts.length;
    const subtitle = institutionName
        ? `${count} ${institutionName} ${pluralize("account", count)} identified`
        : `${count} ${pluralize("account", count)} identified`;

    return (
        <div className={styles.container}>
            <div className={styles.subtitle}>{subtitle}</div>

            <ViewTable
                data={importedAccounts}
                view={connectAccountView}
                config={connectAccountViewConfig}
                className={taskBodyTableStyles.viewTableWithoutTopBorder}
                rowBorders
                enableColumnResizing={false}
                emptyView={() => <span>No accounts found.</span>}
                footer={<TaskBodyCommandIsland selectedTask={selectedTask} />}
            />
        </div>
    );
};
